import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NotificationsContext = createContext(null);

// Single shared poller for notifications so multiple <NotificationBell/> instances
// (desktop + mobile) don't each fire their own request. Polls only when the user
// is authenticated and the tab is visible.
export function NotificationsProvider({ children }) {
  const { user } = useAuth();
  const [data, setData] = useState({ notifications: [], unread_count: 0, overdue_count: 0, due_today_count: 0 });
  const [dismissed, setDismissed] = useState(new Set());
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/notifications`);
      setData(res.data);
    } catch (err) {
      console.warn('Notification poll failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) { setData({ notifications: [], unread_count: 0, overdue_count: 0, due_today_count: 0 }); return; }
    let interval = null;
    const start = () => { if (interval) return; load(); interval = setInterval(load, 60000); };
    const stop = () => { if (interval) { clearInterval(interval); interval = null; } };
    const onVisibility = () => (document.hidden ? stop() : start());
    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => { stop(); document.removeEventListener('visibilitychange', onVisibility); };
  }, [load, user]);

  const dismiss = (id) => setDismissed(p => new Set([...p, id]));
  const _seen = new Set();
  const visible = (data.notifications || []).filter(n => {
    if (dismissed.has(n.id) || _seen.has(n.id)) return false; // dedupe by id → unique React keys
    _seen.add(n.id);
    return true;
  });
  const dismissAll = () => setDismissed(new Set(visible.map(n => n.id)));
  const markRead = () => axios.post(`${API_BASE}/notifications/read`).catch(() => {});

  return (
    <NotificationsContext.Provider value={{ data, loading, visible, dismiss, dismissAll, markRead }}>
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error('useNotifications must be used within NotificationsProvider');
  return ctx;
}
